import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { IconPin, IconPinFilled } from "@tabler/icons-react";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { DesktopRoomLine } from "../../../components/room/DesktopRoomLine";
import { DesktopFollowRoomButton } from "../../../components/room/DesktopFollowRoomButton";
import { Tooltip } from "../../../components/ui/Tooltip";
import Skeleton from "../../../components/skeleton/Skeleton";
import SkeletonTextLine from "../../../components/skeleton/SkeletonTextLine";
import { useApiQuery } from "../../../services/api";
import { createMemoryStore } from "../../../lib/store";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { getRoomLogoUrl } from "../../../domains/room/utils/getRoomLogoUrl";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { usePinRoom } from "../../../domains/room/hooks/usePinRoom";
import { roomLineSubtitle } from "../../../domains/room/utils/roomContentCounts";

/** How many rooms "More rooms" lists before "Show all". */
const MORE_ROOMS_PREVIEW = 3;

// Whether "More rooms" is unfolded. Memory only: coming back to the directory
// in the same session keeps what the reader opened.
const useShowAllRooms = createMemoryStore(false);

/**
 * The rooms directory on a desktop (Figma "D3.explore.rooms"): the page of the
 * sidebar's Rooms, also reached from "See all" in the Explore rail or the
 * breadcrumb of a room: the rooms the reader follows and the public rooms to
 * follow. The pinned room leads "Your rooms" (Figma "D3.explore.rooms.pinned"),
 * and hovering another followed room offers its pin.
 */
export function DesktopRoomsPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { activeLang, contentLang } = useActiveLang();
  const [showAll, setShowAll] = useShowAllRooms();
  const { currentUser } = useCurrentUser();
  const { pinnedRoomId, pin, unpin } = usePinRoom();
  // A visitor with no session follows nothing; the desktop auth gate covers
  // the page, so the public rooms are all it draws behind it.
  const joinedRoomsQuery = useApiQuery(
    "/webapp/private/rooms",
    currentUser === null ? false : {}
  );
  const recommendedRoomsQuery = useApiQuery(
    "/webapp/private/rooms/recommended",
    currentUser === null ? false : {}
  );
  const publicRoomsQuery = useApiQuery("/webapp/public/rooms", {});

  const collator = new Intl.Collator(i18n.resolvedLanguage, {
    sensitivity: "base",
  });
  const isJoinedKnown =
    currentUser === null || joinedRoomsQuery.state === "success";
  // The pinned room first, then the others by name, as on the phone.
  const joinedRooms =
    joinedRoomsQuery.state === "success"
      ? [...joinedRoomsQuery.body.rooms].sort((a, b) => {
          if (a.id === pinnedRoomId) return -1;
          if (b.id === pinnedRoomId) return 1;
          return collator.compare(
            getRoomName(a, activeLang),
            getRoomName(b, activeLang)
          );
        })
      : [];
  const pinnedRoom = joinedRooms.find((room) => room.id === pinnedRoomId);
  const pinnedRoomName =
    pinnedRoom === undefined ? null : getRoomName(pinnedRoom, activeLang);
  const joinedIds = new Set(joinedRooms.map((room) => room.id));
  const recommendedOrder = new Map(
    (recommendedRoomsQuery.state === "success"
      ? recommendedRoomsQuery.body.rooms
      : []
    ).map((room, index) => [room.id, index])
  );
  // The public rooms the reader does not follow: the recommended ones first,
  // in the recommender's order, then the others by name. Private rooms are
  // never listed: their door is the invitation. Empty until the joined rooms
  // are known, or a followed room would show with a Follow button.
  const moreRooms =
    !isJoinedKnown || publicRoomsQuery.state !== "success"
      ? []
      : publicRoomsQuery.body
          .filter((room) => !joinedIds.has(room.id))
          .sort((a, b) => {
            const rankA = recommendedOrder.get(a.id);
            const rankB = recommendedOrder.get(b.id);
            if (rankA !== undefined && rankB !== undefined)
              return rankA - rankB;
            if (rankA !== undefined) return -1;
            if (rankB !== undefined) return 1;
            return collator.compare(
              getRoomName(a, activeLang),
              getRoomName(b, activeLang)
            );
          });
  const visibleMoreRooms = showAll
    ? moreRooms
    : moreRooms.slice(0, MORE_ROOMS_PREVIEW);
  const isLoaded = isJoinedKnown && publicRoomsQuery.state === "success";

  function openRoom(roomId: string): void {
    history.push(`/room/discovery/${roomId}`);
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            back={{ kind: "none" }}
            trail={[]}
            current={t("Rooms")}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <div className="px-10 pb-24">
        <div className="mx-auto flex max-w-desktop-block flex-col gap-10 pt-10">
          <div className="flex max-w-desktop-column flex-col gap-3">
            {isLoaded ? (
              <p className="m-0 text-footnote text-ds-slate-600">
                {t("{{count}} rooms · you follow {{followed}}", {
                  count: joinedRooms.length + moreRooms.length,
                  followed: joinedRooms.length,
                })}
              </p>
            ) : (
              <SkeletonTextLine width={160} fontSize={13} lineHeight={20} />
            )}
            <h1 className="m-0 text-desktop-title font-semibold text-ds-black">
              {t("Rooms")}
            </h1>
            <p className="m-0 text-desktop-body text-ds-slate-700">
              {t(
                "Each room gathers the studies on one topic, selected by Juisci. Some rooms are supported by a partner."
              )}
            </p>
          </div>
          <div className="flex max-w-desktop-column flex-col pt-2">
            {isLoaded ? null : <DirectorySkeleton />}
            {joinedRooms.length === 0 ? null : (
              <DirectorySection
                title={t("Your rooms")}
                count={joinedRooms.length}
              >
                {joinedRooms.map((room) => {
                  const target = {
                    id: room.id,
                    name: getRoomName(room, activeLang),
                  };
                  return (
                    // `group`: the pin of a room shows on the row's hover.
                    <div key={room.id} className="group">
                      <DesktopRoomLine
                        nameBadge={null}
                        name={target.name}
                        logoUrl={getRoomLogoUrl(room)}
                        subtitle={roomLineSubtitle(room, contentLang, t)}
                        action={
                          room.id === pinnedRoomId ? (
                            <Tooltip
                              side="below"
                              content={{ kind: "label", text: t("Unpin room") }}
                              trigger={{
                                kind: "box",
                                className: "inline-flex",
                                control: (descriptionId) => (
                                  <button
                                    type="button"
                                    aria-describedby={descriptionId}
                                    onClick={() => {
                                      void unpin(target);
                                    }}
                                    className="flex h-7 cursor-pointer items-center gap-1 rounded-md border-none bg-transparent px-2 text-footnote font-medium text-ds-slate-500 hover:bg-ds-slate-100"
                                  >
                                    <IconPinFilled className="size-4" />
                                    {t("Pinned")}
                                  </button>
                                ),
                              }}
                            />
                          ) : (
                            <Tooltip
                              side="below"
                              content={{
                                kind: "label",
                                text:
                                  pinnedRoomName === null
                                    ? t("Pin to top")
                                    : t("Pin to top · replaces {{room}}", {
                                        room: pinnedRoomName,
                                      }),
                              }}
                              trigger={{
                                kind: "box",
                                className:
                                  // The fade needs a hover: on a touchscreen (the desktop
                                  // shell on an iPad) a hidden pin would still take the
                                  // taps at the end of the row, so it stays visible.
                                  "inline-flex transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-focus-within:opacity-100 [@media(hover:hover)]:group-hover:opacity-100",
                                control: (descriptionId) => (
                                  <button
                                    type="button"
                                    aria-label={t("Pin to top")}
                                    aria-describedby={descriptionId}
                                    onClick={() => {
                                      void pin(target);
                                    }}
                                    className="flex size-7 cursor-pointer items-center justify-center rounded-md border border-ds-slate-200 bg-ds-white text-ds-slate-600 hover:bg-ds-slate-100"
                                  >
                                    <IconPin className="size-4" stroke={1.5} />
                                  </button>
                                ),
                              }}
                            />
                          )
                        }
                        onClick={() => {
                          openRoom(room.id);
                        }}
                      />
                    </div>
                  );
                })}
              </DirectorySection>
            )}
            {moreRooms.length === 0 ? null : (
              <DirectorySection
                title={t("More rooms")}
                count={moreRooms.length}
              >
                {visibleMoreRooms.map((room) => (
                  <DesktopRoomLine
                    nameBadge={null}
                    key={room.id}
                    name={getRoomName(room, activeLang)}
                    logoUrl={getRoomLogoUrl(room)}
                    subtitle={roomLineSubtitle(room, contentLang, t)}
                    action={<DesktopFollowRoomButton roomId={room.id} />}
                    onClick={() => {
                      openRoom(room.id);
                    }}
                  />
                ))}
                {showAll || moreRooms.length <= MORE_ROOMS_PREVIEW ? null : (
                  <button
                    type="button"
                    onClick={() => {
                      setShowAll(true);
                    }}
                    className="cursor-pointer self-start border-none bg-transparent p-0 text-footnote font-medium text-ds-mint-700 hover:underline"
                  >
                    {t("Show all {{count}} rooms", {
                      count: moreRooms.length,
                    })}
                  </button>
                )}
              </DirectorySection>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** How many lines each section skeleton draws, the preview of "More rooms". */
const SKELETON_LINE_COUNT = MORE_ROOMS_PREVIEW;

/**
 * The two sections while the rooms load: a heading, then lines of the shape
 * of `DesktopRoomLine` with its Follow button. It keeps the column from
 * standing empty under the title.
 */
function DirectorySkeleton(): React.JSX.Element {
  return (
    <>
      {[96, 112].map((titleWidth) => (
        <section key={titleWidth} className="flex flex-col">
          <div className="border-b border-ds-slate-100 pb-2">
            <SkeletonTextLine
              width={titleWidth}
              fontSize={17}
              lineHeight={22}
            />
          </div>
          <div className="flex flex-col gap-4 pt-5 pb-7">
            {Array.from({ length: SKELETON_LINE_COUNT }, (_, index) => (
              <div key={index} className="flex w-full items-center gap-2.5">
                <div className="flex min-w-0 flex-1 items-start gap-2.5">
                  <Skeleton
                    className="shrink-0 rounded-lg"
                    width={32}
                    height={32}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-px">
                    <SkeletonTextLine
                      width="40%"
                      fontSize={13}
                      lineHeight={20}
                    />
                    <SkeletonTextLine
                      width="25%"
                      fontSize={11}
                      lineHeight={16}
                    />
                  </div>
                </div>
                <Skeleton
                  className="shrink-0 rounded-lg"
                  width={64}
                  height={30}
                />
              </div>
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

function DirectorySection({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="flex flex-col">
      <h2 className="m-0 flex items-baseline gap-2 border-b border-ds-slate-100 pb-2 text-heading-xs font-semibold text-ds-black">
        {title}
        <span className="text-footnote font-normal text-ds-slate-600">
          {count}
        </span>
      </h2>
      <div className="flex flex-col gap-4 pt-5 pb-7">{children}</div>
    </section>
  );
}
